import { useEffect, useRef } from 'react';

/**
 * Subscribes to a `window` event for the lifetime of the component.
 *
 * The listener is registered once and removed with the exact same reference on
 * unmount, however often `handler` changes identity between renders. `handler`
 * is always the latest one, so it can safely close over props and state — no
 * refs or `useCallback` needed at the call site.
 *
 * `options` is read once, when the listener is attached; pass a literal.
 *
 * @param {string} eventName e.g. `'scroll'`, `'resize'`
 * @param {Function} handler receives the DOM event
 * @param {Object} [options] `addEventListener` options, e.g. `{ passive: true }`
 */
export const useWindowEvent = (eventName, handler, options) => {
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    const listener = (event) => {
      if (handlerRef.current) handlerRef.current(event);
    };

    window.addEventListener(eventName, listener, options);

    return () => {
      window.removeEventListener(eventName, listener, options);
    };
  }, [eventName]);
};
